import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import ProgressBar from '@yaak/components/src/ProgressBar'
import Grid from '@yaak/components/src/Grid'
import Empty from '@yaak/components/src/Empty'
import Typography, {
  TypographySizes,
  TypographyTypes,
} from '@yaak/components/src/Typography/Typography'
import Divider from '@yaak/components/src/Divider/Divider'
import { GridTextElement } from '@yaak/components/src/Grid/GridElements'
import { useSmallScreenMatches } from '@yaak/components/customHooks/useSmallScreenMatches'
import {
  ToastContext,
  ToastContextType,
} from '@yaak/components/context/toastContext'
import { transpose } from '../../helpers/common'
import { formatTimestamp } from '../../helpers/time'
import { getHealthStatusBadge } from '../../helpers/healthStatus'
import {
  formatSimDeviceInfoValue,
  getCheckLabel,
  getChecksFromPayload,
  getSimDeviceInfo,
  getSnapshotSummaryEntries,
  isPlainObject,
  isStatusKey,
  isTimestampKey,
  parseSimDeviceInfoPayload,
  sortObjectEntries,
} from '../../services/simDeviceInfo'
import overviewStyle from '../SimulationOverview/style.less'
import style from './style.less'
import { useAuth0 } from '@auth0/auth0-react'
import { getBcp47Locale } from '@yaak/ds-admin/src/helpers/helpers'

const NO_DEVICE_INFO_HEADER = 'No device info could be found'
const NO_DEVICE_INFO_CONTENT =
  'Please make sure the monitoring tool had communicated at least once.'

interface SimDeviceInfoProps {
  token: string
  simDeviceId: string
}

const HEADERS = ['Property', 'Value']

const getMappedEntries = (entries: [string, unknown][], matches: boolean) => {
  const rows = entries.map(([property, value]) => [
    GridTextElement({
      text: property,
      ellipsis: matches,
    }),
    GridTextElement({
      text: formatSimDeviceInfoValue(value),
      ellipsis: matches,
    }),
  ])
  const columns = transpose(rows)
  columns.map((row: any, i: number) =>
    HEADERS.map((header, k) => i === k && row.unshift(header))
  )

  return {
    options: {
      fixedColumns: 0,
    },
    rows: columns,
  }
}

const formatDisplayValue = (
  key: string,
  value: unknown,
  locale: string
): React.ReactNode => {
  if (value === null || value === undefined) {
    return ''
  }

  if (isStatusKey(key) && typeof value === 'string') {
    return getHealthStatusBadge(value)
  }

  if (isTimestampKey(key) && typeof value === 'string') {
    return formatTimestamp(value, locale)
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false'
  }

  if (typeof value === 'object') {
    return formatSimDeviceInfoValue(value)
  }

  return String(value)
}

const getMappedDisplayEntries = (
  entries: [string, unknown][],
  matches: boolean,
  locale: string
) => {
  const rows = entries.map(([property, value]) => [
    GridTextElement({
      text: property,
      ellipsis: matches,
    }),
    GridTextElement({
      text: formatDisplayValue(property, value, locale),
      ellipsis: matches,
    }),
  ])
  const columns = transpose(rows)
  columns.map((row: any, i: number) =>
    HEADERS.map((header, k) => i === k && row.unshift(header))
  )

  return {
    options: {
      fixedColumns: 0,
    },
    rows: columns,
  }
}

interface DynamicValueSectionProps {
  title?: string
  value: unknown
  locale: string
  matches: boolean
}

const DynamicValueSection: React.FunctionComponent<DynamicValueSectionProps> = ({
  title,
  value,
  locale,
  matches,
}): ReactElement | null => {
  if (value === null || value === undefined) {
    return null
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return null
    }

    return (
      <div className={style.dynamicSection}>
        {title && (
          <Typography type={TypographyTypes.label} size={TypographySizes.small}>
            {title}
          </Typography>
        )}
        {value.map((item, index) => (
          <div key={`${title ?? 'item'}-${index}`} className={style.nestedBlock}>
            {value.length > 1 && (
              <Typography type={TypographyTypes.label} size={TypographySizes.small}>
                {`${title ?? 'Item'} ${index + 1}`}
              </Typography>
            )}
            <DynamicValueSection
              value={item}
              locale={locale}
              matches={matches}
            />
          </div>
        ))}
      </div>
    )
  }

  if (isPlainObject(value)) {
    const scalarEntries = sortObjectEntries(
      Object.entries(value).filter(([, entryValue]) => !isPlainObject(entryValue))
    )
    const nestedEntries = sortObjectEntries(
      Object.entries(value).filter(([, entryValue]) => isPlainObject(entryValue))
    )
    const arrayEntries = sortObjectEntries(
      Object.entries(value).filter(([, entryValue]) => Array.isArray(entryValue))
    )

    return (
      <div className={style.dynamicSection}>
        {title && (
          <Typography type={TypographyTypes.label} size={TypographySizes.small}>
            {title}
          </Typography>
        )}
        {scalarEntries.length > 0 && (
          <div className={overviewStyle.simulationDrivesWrapperWithoutHeader}>
            <Grid
              headers={HEADERS}
              data={getMappedDisplayEntries(scalarEntries, matches, locale)}
              headerSort={[]}
            />
          </div>
        )}
        {arrayEntries.map(([key, entryValue]) => (
          <DynamicValueSection
            key={key}
            title={key}
            value={entryValue}
            locale={locale}
            matches={matches}
          />
        ))}
        {nestedEntries.map(([key, entryValue]) => (
          <div key={key} className={style.nestedBlock}>
            <DynamicValueSection
              title={key}
              value={entryValue}
              locale={locale}
              matches={matches}
            />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={style.dynamicSection}>
      {title && (
        <Typography type={TypographyTypes.label} size={TypographySizes.small}>
          {title}
        </Typography>
      )}
      <Typography type={TypographyTypes.body}>
        {formatDisplayValue(title ?? 'value', value, locale)}
      </Typography>
    </div>
  )
}

interface CheckResultSectionProps {
  check: Record<string, unknown>
  index: number
  locale: string
  matches: boolean
}

const CheckResultSection: React.FunctionComponent<CheckResultSectionProps> = ({
  check,
  index,
  locale,
  matches,
}): ReactElement => {
  const details = check.details
  const summaryEntries = sortObjectEntries(
    Object.entries(check).filter(([key]) => key !== 'details')
  )

  return (
    <div className={style.checkSection}>
      <Typography type={TypographyTypes.title}>
        {getCheckLabel(check, index)}
      </Typography>
      <div className={overviewStyle.simulationDrivesWrapperWithoutHeader}>
        <Grid
          headers={HEADERS}
          data={getMappedDisplayEntries(summaryEntries, matches, locale)}
          headerSort={[]}
        />
      </div>
      {details !== undefined && (
        <>
          <Divider />
          <Typography type={TypographyTypes.label} size={TypographySizes.small}>
            details
          </Typography>
          <DynamicValueSection value={details} locale={locale} matches={matches} />
        </>
      )}
    </div>
  )
}

const SimDeviceInfoSection: React.FunctionComponent<SimDeviceInfoProps> = ({
  token,
  simDeviceId,
}): ReactElement => {
  const [loading, setLoading] = useState(true)
  const [deviceInfo, setDeviceInfo] = useState<Awaited<
    ReturnType<typeof getSimDeviceInfo>
  >>()

  const { setShowToast } = useContext(ToastContext) as ToastContextType
  const matches = useSmallScreenMatches()
  const { user } = useAuth0()
  const locale = getBcp47Locale(user)

  useEffect(() => {
    const fetchDeviceInfo = async () => {
      setLoading(true)

      const info = await getSimDeviceInfo({
        token,
        simDeviceId,
        onAlert: setShowToast,
      })

      setDeviceInfo(info)
      setLoading(false)
    }

    token && simDeviceId && fetchDeviceInfo()
  }, [token, simDeviceId, setShowToast])

  const parsedPayload = useMemo(() => {
    if (!deviceInfo?.payload) {
      return null
    }

    return parseSimDeviceInfoPayload(deviceInfo.payload)
  }, [deviceInfo?.payload])

  const checks = useMemo(
    () => (parsedPayload ? getChecksFromPayload(parsedPayload) : null),
    [parsedPayload]
  )

  const snapshotSummaryEntries = useMemo(
    () => (parsedPayload ? getSnapshotSummaryEntries(parsedPayload) : []),
    [parsedPayload]
  )

  const flatPayloadEntries = useMemo(() => {
    if (!parsedPayload) {
      return null
    }

    return sortObjectEntries(Object.entries(parsedPayload))
  }, [parsedPayload])

  if (loading) {
    return <ProgressBar />
  }

  if (!deviceInfo) {
    return (
      <Empty header={NO_DEVICE_INFO_HEADER} content={NO_DEVICE_INFO_CONTENT} />
    )
  }

  if (parsedPayload === null) {
    return (
      <Empty
        header={'Invalid device information'}
        content={'The device information payload could not be read.'}
      />
    )
  }

  if (checks && checks.length > 0) {
    return (
      <div className={style.deviceInfoContainer}>
        {snapshotSummaryEntries.length > 0 && (
          <div className={style.snapshotSection}>
            <Typography type={TypographyTypes.title}>Snapshot</Typography>
            <div className={overviewStyle.simulationDrivesWrapperWithoutHeader}>
              <Grid
                headers={HEADERS}
                data={getMappedDisplayEntries(
                  snapshotSummaryEntries,
                  matches,
                  locale
                )}
                headerSort={[]}
              />
            </div>
          </div>
        )}
        <div className={style.checksSection}>
          <Typography type={TypographyTypes.title}>Checks</Typography>
          {checks.map((check, index) => (
            <CheckResultSection
              key={`${getCheckLabel(check, index)}-${index}`}
              check={check}
              index={index}
              locale={locale}
              matches={matches}
            />
          ))}
        </div>
      </div>
    )
  }

  if (flatPayloadEntries && flatPayloadEntries.length > 0) {
    return (
      <div className={overviewStyle.simulationDrivesWrapperWithoutHeader}>
        <Grid
          headers={HEADERS}
          data={getMappedEntries(flatPayloadEntries, matches)}
          headerSort={[]}
        />
      </div>
    )
  }

  return (
    <Empty header={NO_DEVICE_INFO_HEADER} content={NO_DEVICE_INFO_CONTENT} />
  )
}

export default SimDeviceInfoSection
